import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { useToast } from '../components/Toast';
import type { BatchDto, DepartmentDto, Institution, ProgramDto, StudentLogin } from '../types/models';

export function StudentLoginPage() {
  const toast = useToast();
  const { orgId = '' } = useParams();

  const [insts, setInsts] = useState<Institution[]>([]);
  const [depts, setDepts] = useState<DepartmentDto[]>([]);
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [batchOptions, setBatchOptions] = useState<BatchDto[]>([]);
  const [institutionId, setInstitutionId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [programId, setProgramId] = useState('');
  const [batchId, setBatchId] = useState('');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState<StudentLogin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.listInstitutions(orgId), api.listDepartments(orgId), api.listPrograms(orgId)])
      .then(([i, d, p]) => { setInsts(i); setDepts(d); setPrograms(p); })
      .catch(e => setError(e?.message ?? 'Failed to load filters.'));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (!programId) { setBatchOptions([]); return; }
    api.listBatches(programId).then(setBatchOptions).catch(() => setBatchOptions([]));
  }, [programId]);

  const deptsForFilter = useMemo(
    () => depts.filter(d => d.institutionId === institutionId),
    [depts, institutionId],
  );
  const programsForFilter = useMemo(
    () => programs.filter(p => p.departmentId === departmentId),
    [programs, departmentId],
  );

  async function load() {
    setError(null);
    try {
      setRows(await api.listStudentLogins(orgId, batchId || undefined, search || undefined));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load student logins.');
    }
  }

  async function generate() {
    setError(null); setMsg(null);
    if (!batchId) { setError('Select a batch to generate logins for.'); return; }
    setBusy(true);
    try {
      const r = await api.generateStudentLogins(orgId, batchId);
      setMsg(`Generated ${r.created} login(s). ${r.alreadyExisted} already had logins. (${r.totalStaff} students in this batch.)`);
      toast.success(`${r.created} login(s) generated.`);
      await load();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to generate logins.';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword(row: StudentLogin) {
    if (!confirm(`Reset ${row.name}'s password to the default? They'll change it on next login.`)) return;
    setError(null); setMsg(null);
    try {
      await api.resetStudentLoginPassword(row.loginId);
      setMsg(`Password for ${row.name} reset to the default (12345678).`);
      toast.success('Password reset.');
      await load();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to reset password.';
      setError(msg);
      toast.error(msg);
    }
  }

  async function toggleActive(row: StudentLogin) {
    setError(null); setMsg(null);
    try {
      await api.setStudentLoginActive(row.loginId, !row.isActive);
      toast.success('Login status updated.');
      await load();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to update status.';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <OrgLayout>
      <h1 style={{ marginTop: 0 }}>Student Login</h1>

      <div className="card">
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Institution</div>
            <select value={institutionId}
                    onChange={e => { setInstitutionId(e.target.value); setDepartmentId(''); setProgramId(''); setBatchId(''); }}>
              <option value="">All</option>
              {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Department</div>
            <select value={departmentId} disabled={!institutionId}
                    onChange={e => { setDepartmentId(e.target.value); setProgramId(''); setBatchId(''); }}>
              <option value="">All</option>
              {deptsForFilter.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Program</div>
            <select value={programId} disabled={!departmentId}
                    onChange={e => { setProgramId(e.target.value); setBatchId(''); }}>
              <option value="">All</option>
              {programsForFilter.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Batch *</div>
            <select value={batchId} disabled={!programId} onChange={e => setBatchId(e.target.value)}>
              <option value="">Select…</option>
              {batchOptions.map(b => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 180px' }}>
            <div className="muted">Reg / Name</div>
            <input value={search} placeholder="Reg / Name / Login" onChange={e => setSearch(e.target.value)} />
          </label>
          <button onClick={load}>Search</button>
          <button className="primary" onClick={generate} disabled={busy || !batchId}>
            {busy ? 'Generating…' : 'Generate Login'}
          </button>
        </div>
        <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          “Generate Login” creates logins for every student in the selected batch
          (default password <strong>12345678</strong>, changed on first login). Already-generated students are skipped.
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
      {msg && <div className="card" style={{ padding: '8px 16px' }}><span className="muted">{msg}</span></div>}

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Reg No</th>
              <th>Name</th>
              <th>Batch</th>
              <th>Login Name</th>
              <th>Status</th>
              <th>Last Login</th>
              <th style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={8} className="muted">Loading…</td></tr>}
            {rows && rows.length === 0 && (
              <tr><td colSpan={8} className="muted">No student logins yet. Pick a batch and click “Generate Login”.</td></tr>
            )}
            {rows?.map((r, idx) => (
              <tr key={r.loginId}>
                <td>{idx + 1}</td>
                <td className="slug">{r.regNo}</td>
                <td style={{ fontWeight: 600 }}>{r.name}</td>
                <td>{r.batchName}</td>
                <td>{r.loginName}</td>
                <td>
                  <span className="badge" style={!r.isActive ? { background: 'var(--danger-soft-bg)', color: 'var(--danger-soft-fg)' } : undefined}>
                    {r.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {r.passwordChangeNextLogin && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>· must change pw</span>}
                </td>
                <td>{r.lastSuccessfulLoginDt ? new Date(r.lastSuccessfulLoginDt).toLocaleString() : '—'}</td>
                <td>
                  <button className="link-btn" onClick={() => resetPassword(r)}>Reset pw</button>
                  <button className="link-btn" onClick={() => toggleActive(r)}>{r.isActive ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </OrgLayout>
  );
}
